import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { getAllUnits, getFilteredUnits, getUnitConfigurations, getUnitsByConfiguration, type PropertyUnit, type UnitConfiguration, type ExtendedUnitFilters } from '@/lib/units';

interface UseUnitsOptions {
  filters?: ExtendedUnitFilters;
  autoLoad?: boolean;
}

export const useUnits = (options: UseUnitsOptions = {}) => {
  const { filters, autoLoad = true } = options;
  const [units, setUnits] = useState<PropertyUnit[]>([]);
  const [configurations, setConfigurations] = useState<UnitConfiguration[]>([]);
  const [loading, setLoading] = useState(autoLoad);
  const [error, setError] = useState<Error | null>(null);
  const previousFiltersRef = useRef<string>('');
  const configurationsLoadedRef = useRef(false);

  // Serialize filters to detect actual changes
  const filtersKey = useMemo(() => {
    if (!filters || Object.keys(filters).length === 0) return 'no-filters';
    return JSON.stringify(filters);
  }, [filters]);

  const loadUnits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use getFilteredUnits if filters are provided, otherwise getAllUnits
      const { data, error: unitsError } = filters && Object.keys(filters).length > 0
        ? await getFilteredUnits(filters)
        : await getAllUnits();
      if (unitsError) {
        throw new Error(unitsError.message);
      }
      setUnits(data || []);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load units'));
      console.error('Error loading units:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const loadConfigurations = useCallback(async () => {
    // Only load configurations once
    if (configurationsLoadedRef.current) {
      return;
    }
    try {
      const { data, error: configError } = await getUnitConfigurations();
      if (configError) {
        throw new Error(configError.message);
      }
      setConfigurations(data || []);
      configurationsLoadedRef.current = true;
    } catch (err) {
      console.error('Error loading configurations:', err);
    }
  }, []);

  useEffect(() => {
    if (autoLoad) {
      // Only reload units if filters actually changed
      if (previousFiltersRef.current !== filtersKey) {
        previousFiltersRef.current = filtersKey;
        loadUnits();
      }
      // Load configurations only once
      loadConfigurations();
    }
  }, [filtersKey, autoLoad, loadUnits, loadConfigurations]);

  const filteredUnits = useMemo(() => {
    if (!filters) return units;
    
    return units.filter((unit) => {
      if (filters.viewType && unit.view_type !== filters.viewType) return false;
      if (filters.availability && unit.availability !== filters.availability) return false;
      if (filters.category && unit.category !== filters.category) return false;
      if (filters.block && unit.block !== filters.block) return false;
      if (filters.floor) {
        // For fourth (Penthouse), check both floor = 'fourth' OR (floor = 'third' AND 'fourth' in floors_included AND NOT townhouse)
        // This handles 2-storey penthouses that have floor='third' but include 'fourth' in floors_included
        // We exclude townhouses which also have 'fourth' in floors_included but are not penthouses
        if (filters.floor === 'fourth') {
          const isPenthouse = 
            unit.floor === 'fourth' || 
            (unit.floor === 'third' && 
             unit.floors_included && 
             unit.floors_included.includes('fourth') && 
             unit.category !== 'townhouse');
          if (!isPenthouse) {
            return false;
          }
        } else {
          if (unit.floor !== filters.floor) return false;
        }
      }
      if (filters.bedrooms !== undefined && unit.bedrooms !== filters.bedrooms) return false;
      if (filters.bathrooms !== undefined && unit.bathrooms !== filters.bathrooms) return false;
      if (filters.minPrice && unit.price && unit.price < filters.minPrice) return false;
      if (filters.maxPrice && unit.price && unit.price > filters.maxPrice) return false;
      if (filters.minArea && unit.area_total && unit.area_total < filters.minArea) return false;
      if (filters.maxArea && unit.area_total && unit.area_total > filters.maxArea) return false;
      return true;
    });
  }, [units, filters]);

  const sortedUnits = useMemo(() => {
    if (!filters?.sortBy) return filteredUnits;

    const sorted = [...filteredUnits];
    const sortBy = filters.sortBy;
    const sortOrder = filters.sortOrder || 'asc';

    sorted.sort((a, b) => {
      let aValue: string | number | null;
      let bValue: string | number | null;

      switch (sortBy) {
        case 'price':
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
        case 'area_total':
          aValue = a.area_total || 0;
          bValue = b.area_total || 0;
          break;
        case 'bedrooms':
          aValue = a.bedrooms;
          bValue = b.bedrooms;
          break;
        case 'unit_number':
        default:
          aValue = a.unit_number;
          bValue = b.unit_number;
          break;
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredUnits, filters?.sortBy, filters?.sortOrder]);

  return {
    units: sortedUnits,
    configurations,
    loading,
    error,
    refetch: loadUnits,
    refetchConfigurations: loadConfigurations,
  };
};

